import { useTheme } from '@shopify/restyle';
import { IconCalendar, IconChevronLeft, IconChevronRight, IconList } from '@tabler/icons-react-native';
import { EmptyGraphic } from 'app/components/empty-graphic';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { addMonths, localeAwareFormat, startOfMonth, subMonths } from 'app/lib/date';
import type { Theme } from 'app/shopify';
import { type Loan, getLoanETA, LoanETA, LoanStatus, type LoanType } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, View } from 'react-native';
import Animated, { FadeIn, FadeOut, LinearTransition } from 'react-native-reanimated';
import { LoanCalendar } from './loan-calendar';
import { loanIntersectsMonth, loanOccursOnDate } from './loan-calendar-utils';
import { LoanPlanner } from './loan-planner';
import { EmptyLoanPeriod } from './empty-loan-period';
import { DesktopDateLoans } from './desktop-date-loans';
import type { TFunction } from 'i18next';

type DesktopView = 'planner' | 'calendar';

interface DesktopLoansState {
  currentMonth: Date;
  highlightedDate: Date | null;
  highlightedLoanId: Loan['id'] | null;
  selectedDate: Date | null;
  selectedLoanId: Loan['id'] | null;
}

type DesktopLoansAction =
  | { type: 'changeMonth'; month: Date }
  | { type: 'highlightDate'; date: Date | null }
  | { type: 'highlightLoan'; loanId: Loan['id'] | null }
  | { type: 'selectDate'; date: Date }
  | { type: 'selectPlannerLoan'; loanId: Loan['id'] }
  | { type: 'selectDateLoan'; loanId: Loan['id'] };

function createInitialState(): DesktopLoansState {
  return {
    currentMonth: startOfMonth(new Date()),
    highlightedDate: null,
    highlightedLoanId: null,
    selectedDate: null,
    selectedLoanId: null,
  };
}

function desktopLoansReducer(state: DesktopLoansState, action: DesktopLoansAction): DesktopLoansState {
  switch (action.type) {
    case 'changeMonth':
      return {
        currentMonth: startOfMonth(action.month),
        highlightedDate: null,
        highlightedLoanId: null,
        selectedDate: null,
        selectedLoanId: null,
      };
    case 'highlightDate':
      return { ...state, highlightedDate: action.date };
    case 'highlightLoan':
      return { ...state, highlightedLoanId: action.loanId };
    case 'selectDate':
      return {
        ...state,
        currentMonth: startOfMonth(action.date),
        selectedDate: action.date,
        selectedLoanId: null,
      };
    case 'selectPlannerLoan':
      return { ...state, selectedDate: null, selectedLoanId: action.loanId };
    case 'selectDateLoan':
      return { ...state, selectedLoanId: action.loanId };
  }
}

interface ScreenContentProps {
  isLoading: boolean;
  isRefetching: boolean;
  loanType: LoanType;
  loans: Loan[];
  onRefresh: () => void;
  tPrefix: string;
}

interface DesktopLoansBodyProps {
  activeDate: Date | null;
  changeMonth: (month: Date) => void;
  currentMonth: Date;
  highlightDate: (date: Date | null) => void;
  highlightedLoanId: Loan['id'] | null;
  highlightLoan: (loanId: Loan['id'] | null) => void;
  isLoading: boolean;
  loans: Loan[];
  loanType: LoanType;
  monthLoans: Loan[];
  selectDate: (date: Date) => void;
  selectedDate: Date | null;
  selectedDateLoans: Loan[];
  selectedLoanId: Loan['id'] | null;
  selectDateLoan: (loanId: Loan['id']) => void;
  selectPlannerLoan: (loanId: Loan['id']) => void;
  t: TFunction<'loans'>;
  tPrefix: string;
  view: DesktopView;
}

function DesktopLoansBody({
  activeDate,
  changeMonth,
  currentMonth,
  highlightDate,
  highlightedLoanId,
  highlightLoan,
  isLoading,
  loans,
  loanType,
  monthLoans,
  selectDate,
  selectedDate,
  selectedDateLoans,
  selectedLoanId,
  selectDateLoan,
  selectPlannerLoan,
  t,
  tPrefix,
  view,
}: DesktopLoansBodyProps) {
  if (isLoading && loans.length === 0) {
    return <ActivityIndicator accessibilityLabel={t('calendar.loading')} size="large" />;
  }

  if (monthLoans.length === 0) {
    return loans.length === 0 ? (
      <EmptyGraphic
        invertTexts
        primary={t(`${tPrefix}${loanType}.empty.secondLine`)}
        secondary={t(`${tPrefix}${loanType}.empty.firstLine`)}
      />
    ) : (
      <EmptyLoanPeriod loans={loans} month={currentMonth} onMonthChange={changeMonth} />
    );
  }

  if (view === 'planner') {
    return (
      <LoanPlanner
        currentMonth={currentMonth}
        highlightedLoanId={highlightedLoanId}
        loans={monthLoans}
        loanType={loanType}
        onDateHoverIn={highlightDate}
        onDateHoverOut={() => highlightDate(null)}
        onDateSelect={selectDate}
        onLoanHoverIn={highlightLoan}
        onLoanHoverOut={() => highlightLoan(null)}
        onLoanSelect={selectPlannerLoan}
        selectedDate={activeDate}
        selectedLoanId={selectedLoanId}
      />
    );
  }

  return (
    <Column alignSelf="center" gap="spacing.general.x4" maxWidth={820} width="100%">
      <LoanCalendar
        currentMonth={currentMonth}
        highlightedLoanId={highlightedLoanId}
        isDesktop
        loans={monthLoans}
        onDateHoverIn={highlightDate}
        onDateHoverOut={() => highlightDate(null)}
        onDatePress={selectDate}
        onMonthChange={changeMonth}
        selectedDate={selectedDate}
        selectedLoanId={selectedLoanId}
        showHeader={false}
      />
      {selectedDate ? (
        <Animated.View entering={FadeIn.duration(300)} exiting={FadeOut.duration(300)}>
          <DesktopDateLoans
            date={selectedDate}
            loans={selectedDateLoans}
            onLoanHoverIn={highlightLoan}
            onLoanHoverOut={() => highlightLoan(null)}
            onLoanSelect={selectDateLoan}
          />
        </Animated.View>
      ) : null}
    </Column>
  );
}

export function DesktopLoans({
  isLoading,
  loanType,
  loans,
  tPrefix,
}: Pick<ScreenContentProps, 'isLoading' | 'loanType' | 'loans' | 'tPrefix'>) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const [{ currentMonth, highlightedDate, highlightedLoanId, selectedDate, selectedLoanId }, dispatch] = useReducer(
    desktopLoansReducer,
    undefined,
    createInitialState,
  );
  const [view, setView] = useState<DesktopView>('planner');

  const monthLoans = loans.filter(
    (loan) =>
      loanIntersectsMonth(loan, currentMonth) ||
      (loan.status === LoanStatus.Returned &&
        startOfMonth(currentMonth).getTime() === startOfMonth(new Date()).getTime()),
  );
  const selectedDateLoans = selectedDate ? loans.filter((loan) => loanOccursOnDate(loan, selectedDate)) : [];
  const activeDate = highlightedDate ?? selectedDate;
  const attentionCount = monthLoans.filter((loan) => {
    const eta = getLoanETA(loan);
    return eta === LoanETA.late || eta === LoanETA.returned;
  }).length;

  const changeMonth = (month: Date) => {
    dispatch({ type: 'changeMonth', month });
  };

  const selectDate = (date: Date) => {
    dispatch({ type: 'selectDate', date });
  };

  const highlightDate = (date: Date | null) => dispatch({ type: 'highlightDate', date });
  const highlightLoan = (loanId: Loan['id'] | null) => dispatch({ type: 'highlightLoan', loanId });
  const selectPlannerLoan = (loanId: Loan['id']) => dispatch({ type: 'selectPlannerLoan', loanId });
  const selectDateLoan = (loanId: Loan['id']) => dispatch({ type: 'selectDateLoan', loanId });

  return (
    <Column gap="spacing.general.x6" paddingHorizontal="spacing.general.x8" paddingVertical="spacing.general.x8">
      <Row alignItems="flex-end" gap="spacing.general.x6" justifyContent="space-between">
        <Column flex={1}>
          <Text fontWeight="600" variant="headerSmall">
            {t(`${tPrefix}${loanType}.title`)}
          </Text>
          <Row alignItems="center" gap="spacing.general.x2" marginTop="spacing.general.x1">
            <Text color="text.secondary" fontSize={13}>
              {t('calendar.activitySummary', {
                count: monthLoans.length,
                month: localeAwareFormat(currentMonth, 'MMMM'),
              })}
            </Text>
            {attentionCount ? (
              <>
                <View
                  style={{
                    backgroundColor: theme.colors['surface.disabled'],
                    borderRadius: 2,
                    height: 3,
                    width: 3,
                  }}
                />
                <Text color="text.error" fontSize={13} fontWeight="500">
                  {t('calendar.planner.attentionSummary', { count: attentionCount })}
                </Text>
              </>
            ) : null}
          </Row>
        </Column>

        <Row alignItems="center" gap="spacing.general.x3">
          <Row backgroundColor="surface.primary-neutral" borderRadius="radius.md" padding="spacing.general.x1">
            {(
              [
                { icon: IconList, label: t('calendar.planner.views.planner'), value: 'planner' },
                { icon: IconCalendar, label: t('calendar.planner.views.month'), value: 'calendar' },
              ] as const
            ).map(({ icon: Icon, label, value }) => {
              const selected = view === value;

              return (
                <Pressable
                  accessibilityRole="tab"
                  accessibilityState={{ selected }}
                  key={value}
                  onPress={() => setView(value)}
                  style={({ pressed }) => ({
                    alignItems: 'center',
                    backgroundColor: selected ? theme.colors['surface.default'] : 'transparent',
                    borderRadius: theme.borderRadii['radius.sm'],
                    flexDirection: 'row',
                    gap: 6,
                    opacity: pressed ? 0.65 : 1,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    shadowColor: selected ? theme.colors['shadow.strong'] : 'transparent',
                    shadowOffset: { height: 1, width: 0 },
                    shadowOpacity: selected ? 0.1 : 0,
                    shadowRadius: 3,
                  })}
                >
                  <Icon
                    color={selected ? theme.colors['text.primary'] : theme.colors['text.secondary']}
                    size={15}
                    strokeWidth={ICON_STROKE_WIDTH}
                  />
                  <Text color={selected ? 'text.primary' : 'text.secondary'} fontSize={12} fontWeight="600">
                    {label}
                  </Text>
                </Pressable>
              );
            })}
          </Row>

          <Row
            alignItems="center"
            backgroundColor="surface.default"
            borderColor="surface.disabled"
            borderRadius="radius.md"
            borderWidth={1}
            padding="spacing.general.x1"
          >
            <Pressable
              accessibilityLabel={t('calendar.previousMonth')}
              accessibilityRole="button"
              hitSlop={8}
              onPress={() => changeMonth(subMonths(currentMonth, 1))}
              style={({ pressed }) => ({
                alignItems: 'center',
                borderRadius: theme.borderRadii['radius.sm'],
                height: 32,
                justifyContent: 'center',
                opacity: pressed ? 0.5 : 1,
                width: 32,
              })}
            >
              <IconChevronLeft color={theme.colors['text.secondary']} size={18} strokeWidth={ICON_STROKE_WIDTH} />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={() => changeMonth(new Date())}
              style={({ pressed }) => ({
                alignItems: 'center',
                minWidth: 116,
                opacity: pressed ? 0.55 : 1,
                paddingHorizontal: 8,
              })}
            >
              <Text fontSize={13} fontWeight="600">
                {localeAwareFormat(currentMonth, 'MMMM y')}
              </Text>
              <Text color="text.brand" fontSize={10} fontWeight="600">
                {t('calendar.today')}
              </Text>
            </Pressable>
            <Pressable
              accessibilityLabel={t('calendar.nextMonth')}
              accessibilityRole="button"
              hitSlop={8}
              onPress={() => changeMonth(addMonths(currentMonth, 1))}
              style={({ pressed }) => ({
                alignItems: 'center',
                borderRadius: theme.borderRadii['radius.sm'],
                height: 32,
                justifyContent: 'center',
                opacity: pressed ? 0.5 : 1,
                width: 32,
              })}
            >
              <IconChevronRight color={theme.colors['text.secondary']} size={18} strokeWidth={ICON_STROKE_WIDTH} />
            </Pressable>
          </Row>
        </Row>
      </Row>

      <Animated.View layout={LinearTransition.duration(300)}>
        <DesktopLoansBody
          activeDate={activeDate}
          changeMonth={changeMonth}
          currentMonth={currentMonth}
          highlightDate={highlightDate}
          highlightedLoanId={highlightedLoanId}
          highlightLoan={highlightLoan}
          isLoading={isLoading}
          loans={loans}
          loanType={loanType}
          monthLoans={monthLoans}
          selectDate={selectDate}
          selectedDate={selectedDate}
          selectedDateLoans={selectedDateLoans}
          selectedLoanId={selectedLoanId}
          selectDateLoan={selectDateLoan}
          selectPlannerLoan={selectPlannerLoan}
          t={t}
          tPrefix={tPrefix}
          view={view}
        />
      </Animated.View>
    </Column>
  );
}
